import { getBgmVolume } from "../mixSettings.js";
import { getAudioContext } from "../audioContext.js";

export type AudioLike = {
  src: string;
  loop: boolean;
  volume: number;
  currentTime: number;
  play: () => Promise<void>;
  pause: () => void;
};

export function createMp3BgmPlayer(
  src: string,
  createAudio: () => AudioLike = () => new Audio(),
) {
  let element: AudioLike | null = null;
  let playing = false;
  let requestVersion = 0;
  let inFlight: Promise<boolean> | null = null;
  let source: MediaElementAudioSourceNode | null = null;
  let gain: GainNode | null = null;

  function audio(): AudioLike {
    element ??= createAudio();
    return element;
  }

  function halt(node: AudioLike): void {
    try {
      node.pause();
      node.currentTime = 0;
    } catch {
      // jsdom and unloaded media can reject pause/seek
    }
  }

  async function beginStart(): Promise<boolean> {
    const request = ++requestVersion;
    const node = audio();
    node.src = src;
    node.loop = true;
    const volume = getBgmVolume();
    const context = getAudioContext();
    if (context) {
      if (!source || !gain) {
        source = context.createMediaElementSource(node as HTMLMediaElement);
        gain = context.createGain();
        source.connect(gain).connect(context.destination);
      }
      gain.gain.value = volume;
      node.volume = 1;
      if (context.state === "suspended") void context.resume().catch(() => undefined);
    } else {
      node.volume = volume;
    }
    try {
      await node.play();
    } catch {
      inFlight = null;
      return false;
    }
    if (request !== requestVersion) {
      halt(node);
      inFlight = null;
      return false;
    }
    playing = true;
    inFlight = null;
    return true;
  }

  function start(): Promise<boolean> {
    if (playing) return Promise.resolve(true);
    inFlight ??= beginStart();
    return inFlight;
  }

  function stop(): void {
    requestVersion += 1;
    if (!playing && !element) return;
    playing = false;
    if (element) halt(element);
  }

  function setVolume(volume: number): void {
    if (!element) return;
    if (gain) gain.gain.value = volume;
    else element.volume = volume;
  }

  return { start, stop, isPlaying: () => playing, setVolume };
}
